import axios from "axios";
import { IUserContactInfo } from "../../../Models/Admin/IUserContactInfo";
import { IUserInvoiceInfo } from "../../../Models/Admin/IUserInvoiceInfo";
import { IUserListItem } from "../../../Models/Admin/IUserListItem";
import { IUserRoles } from "../../../Models/Admin/IUserRoles";
import { IAttendee } from "../../../Models/Attendees/IAttendee";
import { IResult } from "../../../Models/Result/IResult";
import { dateToStr, strToDate } from "../../../Tools/DateTime";
import { formatUrl, handleError } from "../ApiHelpers";
import { IAdminApi } from "./IAdminApi";

export class AdminApi implements IAdminApi {

    private backendUrl: string;

    constructor(url: string) {
        this.backendUrl = url;
    }

    async addUser(contactInfo: IUserContactInfo, invoiceInfo: IUserInvoiceInfo): Promise<IResult<string>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/AddUser`);
            const response = await axios.post(url, {
                contactInfo,
                invoiceInfo
            });
            return response.data as IResult<string>;
        }
        catch (error) {
            return handleError(error, "saveUserContactInfo");
        }
    }

    async getUsers(text: string, role: string, aktivni: boolean): Promise<IResult<IUserListItem[]>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/GetUsers?text=${text}&role=${role}&aktivni=${aktivni}`);
            const response = await axios.get(url);
            return response.data as IResult<IUserListItem[]>;
        }
        catch (error) {
            return handleError(error, "getUsers");
        }
    }    

    async findAttendees(name: string): Promise<IResult<IAttendee[]>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/findAttendees?name=${name}`);
            const response = await axios.get(url);
            var result = response.data as IResult<IAttendee[]>;
            if (result.data) {
                result.data = result.data.map(x => {
                    return { ...x, dateOfBirth: strToDate(x.dateOfBirth.toString()) }
                });
            }
            return response.data as IResult<IAttendee[]>;
        }
        catch (error) {
            return handleError(error, "findAttendees");
        }
    }    

    async getUserContactInfo(id: string): Promise<IResult<IUserContactInfo>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/GetUserContactInfo?id=${id}`);
            const response = await axios.get(url);
            return response.data as IResult<IUserContactInfo>;
        }
        catch (error) {
            return handleError(error, "getUserContactInfo");
        }
    }
    async getUserInvoiceInfo(id: string): Promise<IResult<IUserInvoiceInfo>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/GetUserInvoiceInfo?id=${id}`);
            const response = await axios.get(url);
            return response.data as IResult<IUserInvoiceInfo>;
        }
        catch (error) {
            return handleError(error, "getUserInvoiceInfo");
        }
    }


    async saveUserContactInfo(info: IUserContactInfo): Promise<IResult<string>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/SaveUserContactInfo`);
            const response = await axios.post(url, info);
            return response.data as IResult<string>;
        }
        catch (error) {
            return handleError(error, "saveUserContactInfo");
        }
    }

    async saveUserInvoiceInfo(info: IUserInvoiceInfo): Promise<IResult<string>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/SaveUserInvoiceInfo`);
            const response = await axios.post(url, info);
            return response.data as IResult<string>;
        }
        catch (error) {
            return handleError(error, "saveUserInvoiceInfo");
        }
    }

    async getUserRoles(id: string): Promise<IResult<IUserRoles>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/GetUserRoles?id=${id}`);
            const response = await axios.get(url);
            return response.data as IResult<IUserRoles>;
        }
        catch (error) {
            return handleError(error, "getUserInvoiceInfo");
        }
    }

    async addUserToRoles(id: string, roles: IUserRoles): Promise<IResult<string>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/AddUserToRoles?id=${id}`);
            const response = await axios.post(url, roles);
            return response.data as IResult<string>;
        }
        catch (error) {
            return handleError(error, "saveUserContactInfo");
        }
    }

    async getAttendees(userId: string): Promise<IResult<IAttendee[]>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/GetAttendees?userId=${userId}`);
            const response = await axios.get(url);

            var result = response.data as IResult<IAttendee[]>;
            if (result.data) {
                result.data = result.data.map(x => {                    
                    return { ...x, dateOfBirth: strToDate(x.dateOfBirth.toString())}
                });
            }
            return result;
        }
        catch (error) {
            return handleError(error, "getAttendees");
        }
    }

    async addAttendee(attendee: IAttendee): Promise<IResult<number>> {
        try {
            const a = { ...attendee }
            a.dateOfBirth = dateToStr(attendee.dateOfBirth as Date)
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/AddAttendee`);
            const response = await axios.post(url, a);
            return response.data as IResult<number>;
        }
        catch (error) {
            return handleError(error, "addAttendee");
        }
    }

    async updateAttendee(attendee: IAttendee): Promise<IResult<boolean>> {
        try {
            const a = { ...attendee }
            a.dateOfBirth = dateToStr(attendee.dateOfBirth as Date)
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/UpdateAttendee/${attendee.id}`);
            const response = await axios.put(url, a);
            return response.data as IResult<boolean>;
        }
        catch (error) {
            return handleError(error, "updateAttendee");
        }
    }

    async deleteAttendee(id: number, userId:string): Promise<IResult<boolean>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/DeleteAttendee/${id}?userId=${userId}`);
            const response = await axios.delete(url);
            return response.data as IResult<boolean>;
        }
        catch (error) {
            return handleError(error, "deleteAttendee");
        }
    }

    async sendNewRegistrationEmail(userId: string): Promise<IResult<boolean>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/sendNewRegistrationEmail/${userId}`);
            const response = await axios.post(url);
            return response.data as IResult<boolean>;
        }
        catch (error) {
            return handleError(error, "sendNewRegistrationEmail");
        }
    }

    async sendNewRegistrationEmailWithPassword(userId: string): Promise<IResult<boolean>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/sendNewRegistrationEmailWithPassword/${userId}`);
            const response = await axios.post(url);
            return response.data as IResult<boolean>;
        }
        catch (error) {
            return handleError(error, "sendNewRegistrationEmail");
        }
    }

    async getUserInRole(roleName: string, filter: string): Promise<IResult<IUserListItem[]>> {
        try {
            
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/GetUsersInRole?roleName=${roleName}&filter=${filter}`);
            const response = await axios.get(url);
            return response.data as IResult<IUserListItem[]>;
        }
        catch (error) {
            return handleError(error, "getUserInRole");
        }
    }

    async removeUser(userId: string): Promise<IResult<null>> {
        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/removeUser?userId=${userId}`);
            const response = await axios.delete(url);
            return response.data as IResult<null>;
        }
        catch (error) {
            return handleError(error, "removeUser");
        }
    }

    async changeUserState(userId: string, active: boolean): Promise<IResult<null>> {

        try {
            const url = formatUrl(this.backendUrl, `/api/UserAdmin/changeUserState?userId=${userId}&active=${active}`);
            const response = await axios.post(url);
            return response.data as IResult<null>;
        }
        catch (error) {
            return handleError(error, "removeUser");
        }
    }


}
